"use strict";
/*
 * Membrane React package
 * {@link https://www.npmjs.com/package/@membranehq/react}
 * v0.29.2
 */
'use client';

import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { getReferenceCollectionPointerForSchema, getMissingRequiredFields, extractMembraneErrorData } from '@membranehq/sdk';
import { DropdownUI } from '../../../../DropdownUI/index.mjs';
import { SvgIconType } from '../../../../SvgIcon/svg-icon-type.mjs';
import { useIntegrationAppConnection } from '../../../../../contexts/use-integration-app-connection.mjs';
import { useIntegrationAppIntegration } from '../../../../../contexts/use-integration-app-integration.mjs';
import { useMembraneClient } from '../../../../../contexts/use-membrane-client.mjs';
import { useThrottle } from '../../../../../helpers/hooks/useThrottle.mjs';
import { withSlotScopeClasses } from '../../../../../helpers/scoped-slots.mjs';
import { simpleUniqueId } from '../../../../../helpers/simple-unique-id.mjs';
import { useDataCollectionSpec } from '../../../../../hooks/data-collections/useDataCollectionSpec.mjs';
import { memo, useState, useRef, useEffect } from 'react';
import { tv } from 'tailwind-variants';
import { Root, Icon, Title } from '../../../../../ui-kit/styled/alert.mjs';
import { SvgIcon } from '../../../../SvgIcon/index.mjs';
import { useComboBox } from '../../../context/use-combobox.mjs';
import { useComboBoxDropdownContext } from '../../../context/use-combobox-dropdown.mjs';
import { useComboBoxDropdownSearchContext } from '../../../context/use-combobox-dropdown-search.mjs';
import { filterOptionsByLabel } from '../../../options-factories/utils.mjs';
import { useKeyboardBinding } from '../../KeyboardNavigation/useKeyboardBinding.mjs';
import { VirtualizedDropdownList } from '../../VirtualizedDropdownList.mjs';
import { ParametersPanel } from './ParametersPanel.mjs';

const lookupOptionsRecipe = tv({
  slots: withSlotScopeClasses("lookup-config-options", {
    container: "flex flex-col max-w-md overflow-y-auto",
    "required-message-container": "h-fit w-fit rounded-lg px-3 py-1.5 items-center gap-2 bg-gray-2 mx-1.5",
    "required-message-title": "text-sm leading-5 text-slate-11 font-medium p-0",
    "additional-message-container": "h-fit w-fit rounded-lg px-3 py-1.5 items-center gap-2 bg-amber-2 border-amber-8 mx-1.5 mt-1.5 [&_svg]:text-amber-11",
    "additional-message-title": "text-sm leading-5 text-amber-11 font-medium p-0"
  })
});
const styles = lookupOptionsRecipe();
const LookupOptionsCategoryContent = memo(function({
  onOptionSelect,
  children
}) {
  const [options, setOptions] = useState([]);
  const [findByIdOption, setFindByIdOption] = useState();
  const [isLoading, setLoadingOptions] = useState(true);
  const [additionalMessage, setAdditionalMessage] = useState("");
  const [error, setError] = useState();
  const { dropdownSearchInputValue } = useComboBoxDropdownSearchContext();
  const { schema } = useComboBox();
  const throttledSearchValue = useThrottle(dropdownSearchInputValue, 250);
  const pointer = getReferenceCollectionPointerForSchema(schema);
  const [parametersValue, setParametersValue] = useState(pointer?.parameters || {});
  const { client } = useMembraneClient();
  const { connectionId } = useIntegrationAppConnection();
  const { integrationId } = useIntegrationAppIntegration();
  const currentLoadOptionsId = useRef("");
  const { dataCollectionSpec: referenceCollectionSpec } = useDataCollectionSpec({
    key: pointer?.key,
    integrationId
  });
  useEffect(() => {
    if (pointer?.parameters) {
      setParametersValue(pointer.parameters);
    }
  }, [JSON.stringify(pointer)]);
  useEffect(() => {
    if (schema && connectionId) {
      void loadOptions(throttledSearchValue);
    }
  }, [
    throttledSearchValue,
    connectionId,
    JSON.stringify(schema),
    JSON.stringify(parametersValue),
    JSON.stringify(referenceCollectionSpec)
  ]);
  if (!schema) return null;
  const hasParameters = Object.keys(referenceCollectionSpec?.parametersSchema?.properties ?? {}).length > 0;
  const hasMissingParameters = getMissingRequiredFields(referenceCollectionSpec?.parametersSchema, parametersValue)?.length > 0;
  async function loadOptions(searchValue) {
    const loadOptionsId = simpleUniqueId();
    currentLoadOptionsId.current = loadOptionsId;
    setLoadingOptions(true);
    setError(void 0);
    setFindByIdOption(void 0);
    setAdditionalMessage("");
    if (hasMissingParameters) return;
    try {
      if (!connectionId) {
        throw new Error("No connection provided. Cannot load options.");
      }
      if (!referenceCollectionSpec || !pointer) {
        setOptions([]);
        return;
      }
      const connectionAccessor = client.connection(connectionId);
      let response;
      const collectionAccessor = connectionAccessor.dataCollection(pointer?.key, parametersValue);
      if (referenceCollectionSpec.findById) {
        try {
          const itemById = await collectionAccessor.findById({
            id: searchValue
          });
          setFindByIdOption({
            label: itemById.record.name ?? itemById.record.id,
            value: itemById.record.id,
            hint: itemById.record.id,
            hintPrefix: "ID:"
          });
        } catch {
        }
      }
      if (!referenceCollectionSpec?.list) {
        throw new Error(`Collection "${referenceCollectionSpec.key}" does not support listing options.`);
      }
      if (searchValue && referenceCollectionSpec.search) {
        try {
          response = await collectionAccessor.search({
            query: searchValue
          });
          response.cursor = void 0;
        } catch {
        }
      }
      if (!searchValue && !response && referenceCollectionSpec?.list) {
        response = await collectionAccessor.list();
      }
      if (currentLoadOptionsId.current === loadOptionsId) {
        const options2 = response?.records.map((record) => {
          return {
            label: record.name ?? record.id,
            value: record.id,
            hint: record.id,
            hintPrefix: "ID:"
          };
        });
        if (response?.cursor && !!options2) {
          setAdditionalMessage(
            `Only the first ${options2.length} options are loaded. If your option is not in the list, please type its ID manually.`
          );
        }
        setOptions(options2 || []);
      }
    } catch (e) {
      if (currentLoadOptionsId.current === loadOptionsId) {
        setError(extractMembraneErrorData(e));
      }
    } finally {
      if (currentLoadOptionsId.current === loadOptionsId) {
        setLoadingOptions(false);
      }
    }
  }
  const filteredOptions = filterOptionsByLabel(options, dropdownSearchInputValue, true, ["value"]);
  const aggregatedOptions = aggregateOptions(filteredOptions, dropdownSearchInputValue, findByIdOption);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    hasParameters && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        ParametersPanel,
        {
          onChange: (value) => setParametersValue(value),
          schema: referenceCollectionSpec?.parametersSchema,
          value: parametersValue
        }
      ),
      /* @__PURE__ */ jsx("hr", { className: "my-1.5 border-0 border-t border-border-default" })
    ] }),
    /* @__PURE__ */ jsx("div", { className: styles.container(), children: /* @__PURE__ */ jsxs(DropdownUI.List, { children: [
      children,
      /* @__PURE__ */ jsx("hr", { className: "my-1.5 border-0 border-t border-border-default" }),
      /* @__PURE__ */ jsx(DropdownUI.ListHeader, { children: "Available options" }),
      /* @__PURE__ */ jsx(
        LookupOptionsList,
        {
          fallbackOptions: options,
          options: aggregatedOptions,
          onOptionSelect: (value) => onOptionSelect(value?.value)
        }
      ),
      hasMissingParameters && /* @__PURE__ */ jsx(OptionsParametersRequiredAlert, {}),
      !hasMissingParameters && !error && additionalMessage && /* @__PURE__ */ jsx(AdditionalMessageAlert, { text: additionalMessage }),
      !hasMissingParameters && isLoading && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(DropdownUI.LoadingItem, {}),
        /* @__PURE__ */ jsx(DropdownUI.LoadingItem, {}),
        /* @__PURE__ */ jsx(DropdownUI.LoadingItem, {})
      ] }),
      !hasMissingParameters && !isLoading && error && /* @__PURE__ */ jsx(DropdownUI.ErrorHeading, { text: error?.message || "Something went wrong" })
    ] }) })
  ] });
});
function LookupOptionsList({
  options,
  fallbackOptions,
  onOptionSelect
}) {
  const { isOptionSelected } = useComboBoxDropdownContext();
  const deduplicatedFallback = options.length > 0 ? [] : fallbackOptions;
  useKeyboardBinding({
    options,
    fallbackOptions: deduplicatedFallback
  });
  function handleOptionSelect(option) {
    onOptionSelect(option);
  }
  return /* @__PURE__ */ jsx(DropdownUI.List, { className: "[&:not(:last-child)]:mb-1", children: /* @__PURE__ */ jsx(
    VirtualizedDropdownList,
    {
      options,
      fallbackOptions: deduplicatedFallback,
      isOptionSelected,
      handleOptionSelect
    }
  ) });
}
function OptionsParametersRequiredAlert() {
  const styles2 = lookupOptionsRecipe();
  return /* @__PURE__ */ jsxs(Root, { className: styles2["required-message-container"](), children: [
    /* @__PURE__ */ jsx(Icon, { asChild: true, children: /* @__PURE__ */ jsx(SvgIcon, { type: SvgIconType.InfoCircle }) }),
    /* @__PURE__ */ jsx(Title, { className: styles2["required-message-title"](), children: "Please select parameters above to display lists" })
  ] });
}
function AdditionalMessageAlert({ text }) {
  const styles2 = lookupOptionsRecipe();
  return /* @__PURE__ */ jsxs(Root, { className: styles2["additional-message-container"](), children: [
    /* @__PURE__ */ jsx(Icon, { asChild: true, children: /* @__PURE__ */ jsx(SvgIcon, { type: SvgIconType.InfoCircle }) }),
    /* @__PURE__ */ jsx(Title, { className: styles2["additional-message-title"](), children: text })
  ] });
}
function aggregateOptions(options, searchValue, findByIdOption) {
  const result = [];
  if (searchValue) {
    if (findByIdOption) {
      result.push(findByIdOption);
    } else {
      result.push({
        label: searchValue,
        value: searchValue,
        hint: "custom",
        hintPrefix: "ID:"
      });
    }
  }
  result.push(...options);
  return deduplicateOptions(result);
}
function deduplicateOptions(options) {
  const uniqueOptions = /* @__PURE__ */ new Set();
  return options.filter((option) => {
    if (uniqueOptions.has(option.value)) return false;
    uniqueOptions.add(option.value);
    return true;
  });
}

export { AdditionalMessageAlert, LookupOptionsCategoryContent, OptionsParametersRequiredAlert };
